import React, { useEffect, useMemo, useState, useCallback } from "react";
import { useSearchParams, Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";
import {
  Bell, AlertTriangle, Sparkles, CheckCircle2, ShieldAlert,
  RefreshCw, Search, ChevronDown, ChevronUp, ExternalLink,
  Quote, BadgeCheck, Clock, ArrowRight, ListFilter, Layers, Plus,
} from "lucide-react";

import { api } from "../lib/api";
import AlertDrawer from "../components/AlertDrawer";
import EmptyState from "../components/common/EmptyState";
import CreateAlertDialog from "../components/CreateAlertDialog";

const CATEGORIES = ["critical", "warning", "opportunity", "resolved"];

const CATEGORY_VISUAL = {
  critical:    {
    Icon: ShieldAlert,
    accent: "rose",
    border: "border-rose-200",
    surface: "bg-rose-50/70",
    badge:  "bg-rose-100 text-rose-800",
    bar:    "bg-rose-500",
    iconWrap: "bg-rose-100 text-rose-700",
  },
  warning:     {
    Icon: AlertTriangle,
    accent: "amber",
    border: "border-amber-200",
    surface: "bg-amber-50/70",
    badge:  "bg-amber-100 text-amber-800",
    bar:    "bg-amber-500",
    iconWrap: "bg-amber-100 text-amber-700",
  },
  opportunity: {
    Icon: Sparkles,
    accent: "emerald",
    border: "border-emerald-200",
    surface: "bg-emerald-50/70",
    badge:  "bg-emerald-100 text-emerald-800",
    bar:    "bg-emerald-500",
    iconWrap: "bg-emerald-100 text-emerald-700",
  },
  resolved:    {
    Icon: CheckCircle2,
    accent: "zinc",
    border: "border-zinc-200",
    surface: "bg-zinc-50/70",
    badge:  "bg-zinc-100 text-zinc-700",
    bar:    "bg-zinc-400",
    iconWrap: "bg-zinc-100 text-zinc-600",
  },
};

export default function Alerts() {
  const { t } = useTranslation("alerts");
  const [searchParams, setSearchParams] = useSearchParams();

  const [payload, setPayload] = useState(null);   // {stats, items, filters_meta}
  const [loading, setLoading] = useState(true);
  const [scanning, setScanning] = useState(false);
  const [selectedId, setSelectedId] = useState(null);
  const [search, setSearch] = useState(searchParams.get("search") || "");
  const [createOpen, setCreateOpen] = useState(false);
  const [workspaceDocuments, setWorkspaceDocuments] = useState([]);

  const category = searchParams.get("category") || "critical";
  const domain = searchParams.get("domain") || "";
  const sort = searchParams.get("sort") || "urgency";

  const updateParam = useCallback((key, value) => {
    const sp = new URLSearchParams(searchParams);
    if (value) sp.set(key, value); else sp.delete(key);
    setSearchParams(sp, { replace: true });
  }, [searchParams, setSearchParams]);

  const fetchAlerts = useCallback(async () => {
    setLoading(true);
    try {
      const params = { sort, limit: 200 };
      if (category) params.category = category;
      if (domain) params.domain = domain;
      if (search) params.search = search;
      const r = await api.get("/alerts", { params });
      setPayload(r.data);
    } catch (e) {
      toast.error(e.response?.data?.detail || t("toasts.load_failed"));
    } finally {
      setLoading(false);
    }
  }, [category, domain, search, sort, t]);

  useEffect(() => { fetchAlerts(); }, [fetchAlerts]);

  // Fetch the workspace's documents once to populate the optional "link a
  // document" dropdown in the create-alert dialog.
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const r = await api.get("/documents");
        const docs = Array.isArray(r.data) ? r.data : (r.data?.items || r.data?.documents || []);
        if (!cancelled) {
          setWorkspaceDocuments(docs.map((d) => ({
            id: d.id,
            filename: d.filename || d.name || d.id,
            document_type: d.document_type,
          })));
        }
      } catch {
        // Non-blocking — dropdown just stays empty
      }
    })();
    return () => { cancelled = true; };
  }, []);

  const triggerScan = useCallback(async () => {
    setScanning(true);
    try {
      const r = await api.post("/alerts/scan");
      const created =
        (r.data.opportunities?.created || 0) +
        (r.data.inaction?.created || 0) +
        (r.data.contradictions?.created || 0);
      toast.success(t("intelligence.scan_success", { created }));
      fetchAlerts();
    } catch (e) {
      toast.error(e.response?.data?.detail || t("toasts.action_failed"));
    } finally {
      setScanning(false);
    }
  }, [fetchAlerts, t]);

  const kpis = payload?.stats?.kpis || {};
  const domains = payload?.filters_meta?.domains || [];
  const items = payload?.items || [];

  return (
    <div className="animate-fade-up max-w-7xl" data-testid="alerts-page">
      <Header
        onScan={triggerScan}
        scanning={scanning}
        onOpenCreate={() => setCreateOpen(true)}
      />

      {/* KPI cards (Figma) */}
      <KpiGrid
        kpis={kpis}
        activeCategory={category}
        onPick={(c) => updateParam("category", c)}
      />

      {/* Toolbar : search + domain + sort */}
      <Toolbar
        search={search}
        setSearch={(v) => { setSearch(v); updateParam("search", v); }}
        domain={domain}
        domains={domains}
        onDomain={(v) => updateParam("domain", v)}
        sort={sort}
        onSort={(v) => updateParam("sort", v)}
      />

      {/* Alert list */}
      <section className="mt-4" data-testid="alerts-list-container">
        {loading ? (
          <div className="space-y-3" data-testid="alerts-loading">
            {[0, 1, 2].map((i) => (
              <div key={i} className="h-32 bg-zinc-50 border border-zinc-100 rounded-xl animate-pulse" />
            ))}
          </div>
        ) : items.length === 0 ? (
          <EmptyStateForCategory category={category} />
        ) : (
          <ul className="space-y-3" data-testid="alerts-list">
            {items.map((a) => (
              <AlertCard key={a.id} alert={a} onOpen={() => setSelectedId(a.id)} />
            ))}
          </ul>
        )}
      </section>

      {selectedId && (
        <AlertDrawer
          alertId={selectedId}
          onClose={() => setSelectedId(null)}
          onChanged={fetchAlerts}
        />
      )}

      <CreateAlertDialog
        open={createOpen}
        onClose={() => setCreateOpen(false)}
        onCreated={() => fetchAlerts()}
        workspaceDocuments={workspaceDocuments}
        variant="alert"
      />
    </div>
  );
}

// ---- Header --------------------------------------------------------------
function Header({ onScan, scanning, onOpenCreate }) {
  const { t } = useTranslation("alerts");
  return (
    <header className="mb-6">
      <div className="flex items-start justify-between gap-3">
        <div>
          <div className="flex items-center gap-2.5 mb-1">
            <span className="inline-flex items-center justify-center w-8 h-8 rounded-lg bg-zinc-950 text-white">
              <Bell size={15} strokeWidth={2} />
            </span>
            <h1
              className="font-display font-semibold text-2xl tracking-tight text-zinc-950"
              data-testid="alerts-title"
            >
              {t("intelligence.page_title")}
            </h1>
          </div>
          <p className="text-sm text-zinc-500 max-w-2xl">
            {t("intelligence.page_subtitle")}
          </p>
        </div>
        <div className="flex items-center gap-2">
          <Link
            to="/alerts/register"
            className="text-xs inline-flex items-center gap-1 px-3 py-1.5 rounded-full border border-violet-200 bg-violet-50/40 text-violet-900 hover:bg-violet-100 transition-colors"
            data-testid="alerts-open-register"
          >
            <BadgeCheck size={12} /> {t("actions.open_accepted_risk")}
          </Link>
          <button
            onClick={onOpenCreate}
            className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full border border-zinc-300 bg-white text-xs font-medium text-zinc-900 hover:bg-zinc-50 transition-colors"
            data-testid="create-alert-button-global"
          >
            <Plus size={12} strokeWidth={2.25} />
            {t("create_alert.button")}
          </button>
          <button
            onClick={onScan}
            disabled={scanning}
            className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-zinc-950 text-white text-xs font-medium hover:bg-zinc-800 disabled:opacity-60 transition-colors"
            data-testid="alerts-scan-button"
          >
            <RefreshCw size={12} className={scanning ? "animate-spin" : ""} />
            {scanning ? t("intelligence.scanning") : t("intelligence.scan_now")}
          </button>
        </div>
      </div>
    </header>
  );
}

// ---- KPI cards -----------------------------------------------------------
function KpiGrid({ kpis, activeCategory, onPick }) {
  const { t } = useTranslation("alerts");
  return (
    <section className="grid grid-cols-2 lg:grid-cols-4 gap-3" data-testid="alerts-kpis">
      {CATEGORIES.map((cat) => {
        const vis = CATEGORY_VISUAL[cat];
        const count = kpis[cat] ?? 0;
        const active = activeCategory === cat;
        return (
          <button
            key={cat}
            type="button"
            onClick={() => onPick(cat)}
            data-testid={`kpi-${cat}`}
            className={`group text-left p-4 rounded-xl border-2 transition-all duration-200 ${vis.border} ${vis.surface} ${
              active ? "ring-2 ring-offset-2 ring-zinc-950 scale-[1.01]" : "hover:scale-[1.01]"
            }`}
          >
            <div className="flex items-center justify-between mb-3">
              <span className={`inline-flex items-center justify-center w-9 h-9 rounded-lg ${vis.iconWrap}`}>
                <vis.Icon size={16} strokeWidth={1.75} />
              </span>
              <span className="text-[10.5px] uppercase tracking-[0.16em] font-semibold text-zinc-500">
                {t(`intelligence.categories.${cat}`)}
              </span>
            </div>
            <p
              className="font-display font-bold text-4xl tabular-nums text-zinc-950"
              data-testid={`kpi-value-${cat}`}
            >
              {count}
            </p>
            <p className="text-[11.5px] text-zinc-500 mt-1.5">
              {t(`intelligence.category_descriptions.${cat}`)}
            </p>
            <div className={`mt-3 h-1 rounded-full ${vis.bar} opacity-70 group-hover:opacity-100 transition-opacity`} />
          </button>
        );
      })}
    </section>
  );
}

// ---- Toolbar -------------------------------------------------------------
function Toolbar({ search, setSearch, domain, domains, onDomain, sort, onSort }) {
  const { t } = useTranslation("alerts");
  return (
    <div
      className="mt-6 flex flex-wrap items-center gap-2 p-3 rounded-xl border border-zinc-200 bg-white"
      data-testid="alerts-toolbar"
    >
      <div className="flex-1 min-w-[260px] flex items-center gap-2 px-3 py-1.5 rounded-lg bg-zinc-50 border border-zinc-200">
        <Search size={14} className="text-zinc-400" />
        <input
          type="text"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          placeholder={t("intelligence.search_placeholder")}
          className="flex-1 bg-transparent text-sm outline-none placeholder:text-zinc-400"
          data-testid="alerts-search-input"
        />
      </div>

      <div className="inline-flex items-center gap-1.5">
        <Layers size={14} className="text-zinc-400" />
        <span className="text-[11px] uppercase tracking-wider text-zinc-500">
          {t("intelligence.domain_label")}
        </span>
        <select
          value={domain}
          onChange={(e) => onDomain(e.target.value)}
          className="text-xs border border-zinc-200 rounded-lg px-2 py-1.5 bg-white"
          data-testid="alerts-filter-domain"
        >
          <option value="">{t("intelligence.all_domains")}</option>
          {domains.map((d) => (
            <option key={d} value={d}>{t(`intelligence.domains.${d}`, d)}</option>
          ))}
        </select>
      </div>

      <div className="inline-flex items-center gap-1.5">
        <ListFilter size={14} className="text-zinc-400" />
        <span className="text-[11px] uppercase tracking-wider text-zinc-500">
          {t("intelligence.sort_by")}
        </span>
        <select
          value={sort}
          onChange={(e) => onSort(e.target.value)}
          className="text-xs border border-zinc-200 rounded-lg px-2 py-1.5 bg-white"
          data-testid="alerts-filter-sort"
        >
          <option value="urgency">{t("intelligence.sort_options.urgency")}</option>
          <option value="recent">{t("intelligence.sort_options.recent")}</option>
          <option value="severity">{t("intelligence.sort_options.severity")}</option>
          <option value="impact">{t("intelligence.sort_options.impact")}</option>
        </select>
      </div>
    </div>
  );
}

// ---- Alert card ----------------------------------------------------------
function AlertCard({ alert, onOpen }) {
  const { t } = useTranslation("alerts");
  const [citationsOpen, setCitationsOpen] = useState(false);
  const vis = CATEGORY_VISUAL[alert.category] || CATEGORY_VISUAL.warning;
  const citationCount = (alert.citations || []).length;

  const confidence = typeof alert.confidence === "number" ? alert.confidence : 80;
  const verify = !!alert.verification_required;

  return (
    <li
      className={`rounded-xl border ${vis.border} bg-white overflow-hidden hover:shadow-sm transition-shadow`}
      data-testid={`alert-card-${alert.id}`}
    >
      <div className="p-4 flex items-start gap-3">
        {/* Left: icon + severity dot */}
        <div className={`shrink-0 inline-flex items-center justify-center w-10 h-10 rounded-lg ${vis.iconWrap}`}>
          <vis.Icon size={16} strokeWidth={1.75} />
        </div>

        <div className="flex-1 min-w-0">
          {/* Title row */}
          <div className="flex items-start justify-between gap-3">
            <div className="min-w-0">
              <h3
                className="text-sm font-semibold text-zinc-950 leading-snug"
                data-testid={`alert-card-title-${alert.id}`}
              >
                {alert.title}
              </h3>
              <p className="text-xs text-zinc-500 mt-0.5 line-clamp-2">
                {alert.summary}
              </p>
            </div>
            <div className="flex flex-col items-end gap-1 shrink-0">
              <span className={`text-[10px] px-2 py-0.5 rounded-full font-semibold uppercase tracking-wider ${vis.badge}`}>
                {t(`intelligence.categories.${alert.category}`, alert.category)}
              </span>
              <span className="text-[10px] text-zinc-400 inline-flex items-center gap-1">
                <Clock size={9} />
                {t("intelligence.detected_at", { when: alert.detected_at_label || "" })}
              </span>
            </div>
          </div>

          {/* Meta strip */}
          <div className="mt-3 flex flex-wrap items-center gap-x-3 gap-y-1.5 text-[11px]">
            {alert.domain && (
              <span
                className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded bg-zinc-100 text-zinc-700"
                data-testid={`alert-card-domain-${alert.id}`}
              >
                <Layers size={9} /> {t(`intelligence.domains.${alert.domain}`, alert.domain)}
              </span>
            )}
            <ConfidenceBadge confidence={confidence} verify={verify} t={t} />
            {alert.impact_estimated_formatted && (
              <span
                className="inline-flex items-center text-zinc-600 font-mono"
                data-testid={`alert-card-impact-${alert.id}`}
              >
                {t("intelligence.impact_estimated")}:&nbsp;
                <strong className="text-zinc-900">{alert.impact_estimated_formatted}</strong>
              </span>
            )}
            {alert.value_preserved_formatted && (
              <span
                className="inline-flex items-center text-emerald-700 font-mono"
                data-testid={`alert-card-value-preserved-${alert.id}`}
              >
                {t("intelligence.value_preserved")}:&nbsp;
                <strong>{alert.value_preserved_formatted}</strong>
              </span>
            )}
            {alert.due_at && (
              <span className="inline-flex items-center gap-1 text-zinc-500 font-mono">
                <Clock size={9} /> {alert.due_at?.slice(0, 10)}
              </span>
            )}
          </div>

          {/* Recommended action */}
          {alert.recommended_action && (
            <div
              className="mt-3 p-3 rounded-lg bg-zinc-50/70 border border-zinc-100"
              data-testid={`alert-card-action-${alert.id}`}
            >
              <p className="text-[10px] uppercase tracking-[0.14em] font-semibold text-zinc-500 mb-0.5">
                {t("intelligence.recommended_action")}
              </p>
              <p className="text-xs text-zinc-800">{alert.recommended_action}</p>
            </div>
          )}

          {/* Citations accordion */}
          {citationCount > 0 && (
            <div className="mt-3" data-testid={`alert-card-citations-${alert.id}`}>
              <button
                type="button"
                onClick={() => setCitationsOpen((v) => !v)}
                className="inline-flex items-center gap-1 text-[11px] text-zinc-600 hover:text-zinc-950"
                data-testid={`alert-card-toggle-citations-${alert.id}`}
              >
                <Quote size={11} />
                {citationsOpen
                  ? t("intelligence.hide_citations")
                  : t(citationCount > 1 ? "intelligence.citation_count_plural" : "intelligence.citation_count", { count: citationCount })}
                {citationsOpen ? <ChevronUp size={11} /> : <ChevronDown size={11} />}
              </button>
              {citationsOpen && (
                <ul className="mt-2 space-y-1.5">
                  {alert.citations.map((c, i) => (
                    <li
                      key={i}
                      className="text-[12px] p-2.5 rounded bg-zinc-50 border border-zinc-100"
                      data-testid={`alert-card-citation-${alert.id}-${i}`}
                    >
                      <p className="text-zinc-800 italic leading-snug">« {c.quote} »</p>
                      <div className="mt-1 flex items-center justify-between text-[10px] text-zinc-500">
                        <span>
                          {c.page
                            ? t("intelligence.page_label", { page: c.page })
                            : t("intelligence.page_unknown")}
                          {c.document_title ? ` · ${c.document_title}` : ""}
                        </span>
                        {c.document_id && (
                          <Link
                            to={`/analysis/${c.document_id}`}
                            className="inline-flex items-center gap-0.5 text-zinc-700 hover:text-zinc-950"
                            data-testid={`alert-card-citation-doc-${alert.id}-${i}`}
                          >
                            {t("intelligence.view_document")} <ExternalLink size={9} />
                          </Link>
                        )}
                      </div>
                    </li>
                  ))}
                </ul>
              )}
            </div>
          )}

          {/* Footer CTA */}
          <div className="mt-3 flex items-center justify-end gap-2">
            {alert.source_type === "contradiction" && alert.document?.id && (
              <Link
                to={`/lineage/${alert.document.id}`}
                className="text-[11px] text-violet-700 hover:text-violet-950 inline-flex items-center gap-1 font-medium"
                data-testid={`alert-card-lineage-link-${alert.id}`}
              >
                Voir la chaîne <ExternalLink size={10} />
              </Link>
            )}
            {alert.document?.id && (
              <Link
                to={`/analysis/${alert.document.id}`}
                className="text-[11px] text-zinc-500 hover:text-zinc-950 inline-flex items-center gap-1"
                data-testid={`alert-card-doc-link-${alert.id}`}
              >
                {alert.document.title || t("intelligence.view_document")}
                <ExternalLink size={10} />
              </Link>
            )}
            <button
              type="button"
              onClick={onOpen}
              className="text-xs font-medium px-3 py-1.5 rounded-full bg-zinc-950 text-white hover:bg-zinc-800 inline-flex items-center gap-1"
              data-testid={`alert-card-open-${alert.id}`}
            >
              {t("actions.fix_now")} <ArrowRight size={11} />
            </button>
          </div>
        </div>
      </div>
    </li>
  );
}

function ConfidenceBadge({ confidence, verify, t }) {
  const tone =
    confidence >= 85 ? "bg-emerald-50 text-emerald-800 border-emerald-200"
    : confidence >= 70 ? "bg-amber-50 text-amber-800 border-amber-200"
    : "bg-rose-50 text-rose-800 border-rose-200";
  return (
    <span
      className={`inline-flex items-center gap-1 px-1.5 py-0.5 rounded border ${tone}`}
      data-testid="alert-confidence-badge"
    >
      <BadgeCheck size={9} />
      {t("intelligence.confidence_label")}: <strong className="ml-0.5">{confidence}%</strong>
      {verify && <span className="ml-1 text-[9.5px] uppercase tracking-wider">⚠ {t("intelligence.verification_required")}</span>}
    </span>
  );
}

// ---- Empty state ---------------------------------------------------------
function EmptyStateForCategory({ category }) {
  const { t } = useTranslation("alerts");
  const vis = CATEGORY_VISUAL[category] || CATEGORY_VISUAL.warning;
  // Contextual messaging per category — moves the page closer to the
  // "smart empty state" we want everywhere.
  const titles = {
    critical: t("empty.critical_title", { defaultValue: "Aucun risque critique" }),
    warning: t("empty.warning_title", { defaultValue: "Aucune alerte d'avertissement" }),
    opportunity: t("empty.opportunity_title", { defaultValue: "Aucune opportunité détectée" }),
    resolved: t("empty.resolved_title", { defaultValue: "Aucune alerte résolue" }),
  };
  const subtitles = {
    critical: t("empty.critical_subtitle", {
      defaultValue: "Vos documents sont alignés. Guardian™ continue de surveiller chaque changement.",
    }),
    warning: t("empty.warning_subtitle", {
      defaultValue: "Aucun point d'attention à traiter pour l'instant.",
    }),
    opportunity: t("empty.opportunity_subtitle", {
      defaultValue: "Continuez à uploader des documents pour identifier de la valeur préservable.",
    }),
    resolved: t("empty.resolved_subtitle", {
      defaultValue: "Les alertes résolues apparaîtront ici dès que vous les fermerez.",
    }),
  };
  return (
    <EmptyState
      icon={vis.Icon}
      title={titles[category] || t("intelligence.no_results")}
      subtitle={subtitles[category]}
      testId="alerts-empty-state"
    />
  );
}
